import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core"
import { useCallback, useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { PageHeader, PageLayout } from "../../../components/crud"
import type { Column } from "../../../components/DataTable"
import { DataTable, useDataTable } from "../../../components/DataTable"
import { useUser } from "../../user/hooks/useUser"
import type { Tag } from "../models/tag"
import { TagSchema } from "../models/tag"
import type { DrawerMode } from "./TagDrawer"
import TagDrawer from "./TagDrawer"

export default function TagList() {
  const { t } = useTranslation()
  const { data: userData } = useUser()
  const canManage = userData?.can?.viewMenuAdmin ?? false

  const {
    data,
    isLoading,
    page,
    pageSize,
    sort,
    sortDir,
    handleSortChange,
    handlePageChange,
    filters,
    updateFilters,
  } = useDataTable<Tag>({
    queryKey: "tags",
    url: "post-tag",
    schema: TagSchema,
    defaultSort: { field: "order", dir: "asc" },
    defaultPageSize: 50,
  })

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [drawerOpened, setDrawerOpened] = useState(false)
  const [drawerMode, setDrawerMode] = useState<DrawerMode>("view")

  const params = useParams<{ "*"?: string }>()
  const routeId = params["*"] ? Number(params["*"].split("/")[0]) : NaN

  const openDrawer = useCallback((id: number | null, mode: DrawerMode) => {
    setSelectedId(id)
    setDrawerMode(mode)
    setDrawerOpened(true)
  }, [])

  const closeDrawer = useCallback(() => {
    setDrawerOpened(false)
    setSelectedId(null)
  }, [])

  useEffect(() => {
    if (!Number.isNaN(routeId) && routeId > 0) {
      openDrawer(routeId, "view")
    }
  }, [routeId, openDrawer])

  const handleDeleted = useCallback(() => {
    closeDrawer()
  }, [closeDrawer])
  const handleSwitchToEdit = useCallback(() => {
    setDrawerMode("edit")
  }, [])

  const handleRowClick = useCallback(
    (v: Tag) => {
      if (selectedId === v.id && drawerOpened) {
        closeDrawer()
      } else {
        openDrawer(v.id, "view")
      }
    },
    [openDrawer, closeDrawer, selectedId, drawerOpened],
  )

  const columns: Column<Tag>[] = [
    { key: "id", label: "ID", sortable: true, width: 60 },
    {
      key: "name",
      label: t("tags.name"),
      sortable: true,
      render: (v) => (
        <Text fw={500} size="sm">
          {v.name}
        </Text>
      ),
    },
    {
      key: "order",
      label: t("tags.order"),
      sortable: true,
      width: 100,
      render: (v) => (
        <Badge color="gray" variant="light" size="sm">
          {v.order}
        </Badge>
      ),
    },
  ]

  return (
    <PageLayout
      header={
        <PageHeader
          title={t("tags.title")}
          actions={
            canManage ? (
              <Button
                onClick={() => openDrawer(null, "create")}
                leftSection={<i className="fa fa-plus" />}
              >
                <Trans i18nKey="tags.create" />
              </Button>
            ) : undefined
          }
        />
      }
    >
      <Card withBorder p="sm" radius="md" mb="md">
        <Group grow align="flex-end">
          <TextInput
            label={t("tags.name")}
            placeholder={t("tags.name")}
            value={(filters.name as string) ?? ""}
            onChange={(e) =>
              updateFilters({ name: e.currentTarget.value || undefined })
            }
          />
        </Group>
      </Card>
      <DataTable<Tag>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        page={page}
        pageSize={pageSize}
        sort={sort}
        sortDir={sortDir}
        loading={isLoading}
        emptyMessage={t("tags.empty")}
        keyExtractor={(v) => v.id}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        actions={(v) =>
          canManage ? (
            <Group gap={4} justify="flex-end" wrap="nowrap">
              <Tooltip label={t("common.edit")}>
                <ActionIcon
                  variant="subtle"
                  size="md"
                  onClick={(e) => {
                    e.stopPropagation()
                    openDrawer(v.id, "edit")
                  }}
                >
                  <i className="fa fa-pencil" />
                </ActionIcon>
              </Tooltip>
            </Group>
          ) : undefined
        }
      />
      <TagDrawer
        opened={drawerOpened}
        onClose={closeDrawer}
        mode={drawerMode}
        selectedId={selectedId}
        onDeleted={handleDeleted}
        onSwitchToEdit={handleSwitchToEdit}
      />
    </PageLayout>
  )
}
